import { useCallback, useEffect, useId, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { ChevronDown } from "lucide-react";

import { TabPageTitle } from "../../components/navigation/TabPageTitle";
import { todayKey } from "../../lib/format";
import { DatePickerPopover } from "./DatePickerPopover";
import { resolveTodayTopBarTitle, type PeriodMode } from "./todayTopBarTitle";

type TodayTitleCalendarProps = {
  mode: PeriodMode;
  selectedDate: string;
  referenceDate?: string;
  onSelectDate: (date: string) => void;
};

export function TodayTitleCalendar({
  mode,
  selectedDate,
  referenceDate = todayKey(),
  onSelectDate,
}: TodayTitleCalendarProps) {
  const prefersReducedMotion = useReducedMotion();
  const pickerId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const title = resolveTodayTopBarTitle(mode, selectedDate, referenceDate);

  const close = useCallback((focusTrigger = false) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  const handleSelectDate = useCallback(
    (date: string) => {
      onSelectDate(date);
      close();
    },
    [close, onSelectDate],
  );

  return (
    <>
      {open ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="fixed inset-0 z-40 cursor-default"
          onClick={() => close()}
        />
      ) : null}

      <div ref={containerRef} className={`relative min-w-0 ${open ? "z-50" : ""}`}>
        <TabPageTitle
          title={title}
          suffix={
            <button
              ref={triggerRef}
              type="button"
              className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-[oklch(68%_0.025_155)] transition-colors active:bg-[oklch(22%_0.025_165)]"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-controls={open ? pickerId : undefined}
              aria-label={`${title}, open calendar`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => setOpen((value) => !value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setOpen((value) => !value);
                }
              }}
            >
              <motion.span
                aria-hidden
                className="grid place-items-center"
                animate={{ rotate: open ? 180 : 0 }}
                transition={
                  prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 34 }
                }
              >
                <ChevronDown size={22} strokeWidth={2.25} />
              </motion.span>
            </button>
          }
        />

        <DatePickerPopover
          id={pickerId}
          open={open}
          mode={mode}
          selectedDate={selectedDate}
          referenceDate={referenceDate}
          onSelectDate={handleSelectDate}
          onClose={() => close(true)}
        />
      </div>
    </>
  );
}
